import { useNotification } from "hooks/useNotification";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationOptions,
} from "@tanstack/react-query";
import instance, { ApiUrl } from "./axiosInstance";
import { WasteStation, WasteStationDTO } from "types/wasteStation.type";

export enum WasteStationQueryKey {
  GetOne = "get-waste-station",
  GetAll = "get-waste-station-list",
}

export const useWasteStation = (id: number) =>
  useQuery({
    queryKey: [WasteStationQueryKey.GetOne, id],
    queryFn: async (): Promise<WasteStation> => {
      const response = await instance.get(`${ApiUrl.WasteStation}/${id}`);
      return response.data;
    },
  });

interface GetWasteStationListParams {
  includeDeleted?: boolean;
}

export const useWasteStationList = (
  params: GetWasteStationListParams = {},
  settings = {},
) =>
  useQuery({
    queryKey: [WasteStationQueryKey.GetAll, params],
    queryFn: async (): Promise<WasteStation[]> => {
      const response = await instance.get(ApiUrl.WasteStation, {
        params: {
          includeDeleted: params.includeDeleted ? "true" : undefined,
        },
      });
      return response.data;
    },
    ...settings,
  });

export const useCreateWasteStation = () => {
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: WasteStationDTO): Promise<WasteStation> => {
      const response = await instance.post(ApiUrl.WasteStation, payload);
      return response.data;
    },
    onSuccess: () => {
      notification.info(`Mottagarstation skapad`);
      queryClient.invalidateQueries({
        queryKey: [WasteStationQueryKey.GetAll],
      });
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });
};

export const useUpdateWasteStation = (id: number) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: WasteStationDTO) => {
      const response = await instance.put(
        `${ApiUrl.WasteStation}/${id}`,
        payload,
      );
      return response.data;
    },
    onSuccess: (data: WasteStation) => {
      notification.info(`Mottagarstation uppdaterad`);
      queryClient.invalidateQueries({
        queryKey: [WasteStationQueryKey.GetOne, data.id],
      });
      queryClient.invalidateQueries({
        queryKey: [WasteStationQueryKey.GetAll],
      });
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });
};

export const usePatchWasteStation = (id: number, settings = {}) => {
  const notification = useNotification();

  return useMutation({
    mutationFn: async (
      payload: Partial<WasteStationDTO>,
    ): Promise<WasteStation> => {
      const response = await instance.patch(
        `${ApiUrl.WasteStation}/${id}`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      notification.info(`Ändringarna sparade`);
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
    ...settings,
  });
};

export const deleteWasteStation = () => ({
  mutationFn: async (id: number): Promise<void> =>
    (await instance.delete(`${ApiUrl.WasteStation}/${id}`)).data,
});
